import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useExcJpmamV1DestinationQueryServiceDestinationGet } from '@interxfi/api-client/generated/hooks/exc-jpmam-v1-destination-query-service-destination-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcJpmamV1DestinationState } from '@interxfi/types/generated/api';
import { JpmamDestinationEntityMenu } from '../../../../../../../../../../features/vendor/jpmam/destination/jpmam-destination-entity-menu';
import { JpmamDestinationEventSummary } from '../../../../../../../../../../features/vendor/jpmam/destination/jpmam-destination-event';
import { getJpmamDestinationStateListItems } from '../../../../../../../../../../features/vendor/jpmam/destination/jpmam-destination-list-items';
import { getJpmamDestinationDetailPath } from '../../../../../../../../../../features/vendor/jpmam/destination/jpmam-destination-routing';
import { JpmamDestinationStatusPill } from '../../../../../../../../../../features/vendor/jpmam/destination/jpmam-destination-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/jpm/destination/_destination/$destinationId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `jpmam-destination-${params.destinationId}`,
        label: 'Destination',
        to: getJpmamDestinationDetailPath(params.destinationId),
        suffix: (
          <CopyButton
            content={params.destinationId}
            label="Copy Destination ID"
            tooltip={{ copy: params.destinationId, copied: 'Copied Destination ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('JPMAM Destination'),
  component: JpmamDestinationDetail,
});

function getListItems(data: ExcJpmamV1DestinationState | undefined) {
  const items = getJpmamDestinationStateListItems(data);

  return {
    left: [
      items.data.spec.fundNumber,
      items.data.spec.transferType.typeName,
      { ...items.data.spec.beneficiary.name, title: 'Beneficiary Name' },
      { ...items.data.spec.beneficiary.address.formatted, title: 'Beneficiary Address' },
      items.data.spec.bankDetails.routingNumber,
      items.data.spec.bankDetails.accountNumber,
      { ...items.data.spec.bankDetails.accountHolderName, title: 'Name on Account' },
      items.data.spec.bankDetails.accountType,
    ] as const satisfies ReadonlyListItem[],
    right: [items.destinationId, items.data.spec.assetId, items.metadata.createdAt, items.metadata.updatedAt] as const satisfies ReadonlyListItem[],
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load JPMAM destination', 'view this JPMAM destination');

function JpmamDestinationDetail() {
  const { destinationId } = Route.useParams();
  const request = { destinationId } as const;
  const { data, error, isPending } = useExcJpmamV1DestinationQueryServiceDestinationGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.destination), [data?.destination]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <JpmamDestinationStatusPill isPending={showPending} size="large" status={data?.destination?.status} />
          </HorizontalStack>

          <JpmamDestinationEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <JpmamDestinationEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
